import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { loadStripe } from '@stripe/stripe-js'
import { completeUpgrade, fetchPricing, fetchUpgradePreview, redirectPortal, upgradeTier } from './api'
import { useTranslation } from 'react-i18next'
import { notification } from 'antd'
import { isAxiosError } from 'axios'
import type { Tier } from './tier'

export function usePricing() {
    return useQuery({
        queryFn: fetchPricing,
        queryKey: ['pricing'],
        staleTime: 5 * 60 * 1000,
    })
}

/**
 * What an upgrade to the tier costs, fetched fresh each time it is asked for: the amount is prorated from now.
 */
export function useUpgradePreview(tier: Tier | undefined) {
    return useQuery({
        queryFn: () => fetchUpgradePreview(tier as Tier),
        queryKey: ['upgrade-preview', tier],
        enabled: tier !== undefined,
        gcTime: 0,
        staleTime: 0,
    })
}

export function useRedirectPortal() {
    return useMutation({
        mutationFn: redirectPortal,
    })
}

/**
 * Upgrades and pays the rest of the billing period right away. When the bank asks to confirm the payment (3D Secure),
 * Stripe shows that first; the upgrade then completes, or is cancelled when the payment was not confirmed.
 */
export function useUpgradeTier() {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const { data: pricing } = usePricing()

    const confirmPayment = async (clientSecret: string) => {
        const stripe = pricing ? await loadStripe(pricing.publishableKey) : null

        // A failed or cancelled confirmation needs no handling here: completing the upgrade finds the invoice unpaid.
        await stripe?.confirmCardPayment(clientSecret)
    }

    return useMutation({
        mutationFn: async ({ tier, prorationDate }: { tier: Tier; prorationDate?: number }) => {
            const response = await upgradeTier(tier, prorationDate)

            if (!response.clientSecret) {
                return response
            }

            await confirmPayment(response.clientSecret)

            return completeUpgrade()
        },
        onSuccess: (data) => {
            if (data.upgraded) {
                notification.success({
                    message: t('Subscription upgraded successfully'),
                })

                queryClient.invalidateQueries({ queryKey: ['me'] })
                // The plan sets the allowances and prices shown with the usage and the websites.
                queryClient.invalidateQueries({ queryKey: ['companies'] })
                queryClient.invalidateQueries({ queryKey: ['sites'] })
            }
        },
        onError: (error) => {
            // A validation message from the server (for example an address Stripe Tax does not recognise) says what to fix.
            const description = isAxiosError<{ message?: string }>(error) ? error.response?.data?.message : undefined

            notification.error({
                message: t('Failed to upgrade subscription'),
                description,
            })
        },
    })
}
